import {
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from '../services/firebase';
import type { Section, SectionRevision, SectionUserRef } from '../types/tender';
import { shareTender } from './sharingService';

export const fetchTenderSections = async (tenderId: string, assignedUserId?: string) => {
  const sectionsRef = collection(firestore, 'tenders', tenderId, 'sections');
  let q: ReturnType<typeof query> = query(sectionsRef);

  if (assignedUserId) {
    q = query(sectionsRef, where('assignedTo.uid', '==', assignedUserId));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs
    .map((doc) => ({
      ...(doc.data() as Record<string, unknown>),
      id: doc.id,
    }))
    .sort(
      (
        a: { index?: number; [key: string]: unknown },
        b: { index?: number; [key: string]: unknown }
      ) => (a.index || 0) - (b.index || 0)
    );
};

export const saveTenderSection = async (
  tenderId: string,
  sectionId: string,
  content: string,
  title: string,
  index: number,
  status: string = 'in_progress',
  level?: number,
  lastEditedBy?: SectionUserRef
) => {
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  await setDoc(
    docRef,
    {
      id: sectionId,
      content: content,
      wordCount,
      title: title,
      index: index,
      status: status,
      ...(level ? { level } : {}),
      updatedAt: new Date(),
      ...(lastEditedBy ? { lastEditedBy, editHistory: arrayUnion(lastEditedBy) } : {}),
    },
    { merge: true }
  );

  // Create Revision Snapshot (Global Undo)
  if (lastEditedBy) {
    const revisionsRef = collection(
      firestore,
      'tenders',
      tenderId,
      'sections',
      sectionId,
      'revisions'
    );
    // Use timestamp-based ID or auto-ID. Auto-ID is safer for concurrency.
    const revisionDocRef = doc(revisionsRef);
    await setDoc(revisionDocRef, {
      id: revisionDocRef.id,
      sectionId,
      content: content,
      updatedAt: new Date(),
      editor: lastEditedBy,
      revisionNumber: Date.now(), // Simple ordering
    });
  }
};

export const lockTenderSection = async (
  tenderId: string,
  sectionId: string,
  user: SectionUserRef
) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  await updateDoc(docRef, {
    lockedBy: user,
    updatedAt: new Date(),
  });
};

export const unlockTenderSection = async (tenderId: string, sectionId: string) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  await updateDoc(docRef, {
    lockedBy: deleteField(),
    updatedAt: new Date(),
  });
};

export const assignTenderSection = async (
  tenderId: string,
  sectionId: string,
  user: SectionUserRef | null,
  assigner: SectionUserRef | null = null,
  permissions?: string[]
) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  const tenderRef = doc(firestore, 'tenders', tenderId);

  const updates: Record<string, unknown> = {
    assignedTo: user || deleteField(),
    updatedAt: new Date(),
    ...(assigner ? { lastEditedBy: assigner } : {}),
  };

  if (user) {
    updates.status = 'in_progress';
    updates.reviewedBy = deleteField();
  }

  await updateDoc(docRef, updates);

  if (user) {
    // Grant access to the tender by adding to sharedWith
    const tenderUpdates: Record<string, unknown> = {
      sharedWith: arrayUnion(user.uid),
    };

    if (permissions !== undefined) {
      tenderUpdates[`memberPermissions.${user.uid}`] = permissions;
    }

    await updateDoc(tenderRef, tenderUpdates);

    // Share Encryption Key (Secure Sharing)
    try {
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData?.publicKey) {
          await shareTender(tenderId, user.uid, userData.publicKey);
        } else {
          // User has no public key. Secure sharing not possible.
        }
      }
    } catch (_e) {
      // Failed to share tender encryption key
    }
  }
};

export const markSectionReviewed = async (
  tenderId: string,
  sectionId: string,
  status: 'reviewed' | 'in_progress' | 'complete' | 'pending',
  user?: SectionUserRef
) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  const updates: Record<string, unknown> = {
    status,
    updatedAt: new Date(),
  };

  if (status === 'reviewed') {
    if (user) {
      updates.reviewedBy = { ...user, at: new Date() };
    }
    // Clear assignment when marked as reviewed
    updates.assignedTo = deleteField();
  } else {
    updates.reviewedBy = deleteField();
  }

  await updateDoc(docRef, updates);
};

export const createTenderSection = async (
  tenderId: string,
  title: string,
  level: number,
  index: number,
  content: string = '',
  createdBy?: SectionUserRef
) => {
  // Generate a new ID
  const sectionsRef = collection(firestore, 'tenders', tenderId, 'sections');
  const newDocRef = doc(sectionsRef);
  const sectionId = newDocRef.id;

  await saveTenderSection(tenderId, sectionId, content, title, index, 'pending', level, createdBy);
  return sectionId;
};

export const updateTenderSectionIndex = async (
  tenderId: string,
  sectionId: string,
  index: number
) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  await updateDoc(docRef, { index });
};

export const deleteTenderSection = async (tenderId: string, sectionId: string) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  await deleteDoc(docRef);
};

export const resolveTenderSectionSuggestion = async (
  tenderId: string,
  sectionId: string,
  newContent: string | null
) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);

  const updates: Record<string, unknown> = {
    suggested_content: deleteField(),
    cross_boundary_suggestion: deleteField(),
    suggested_by: deleteField(),
    has_suggestion: false,
    updatedAt: new Date(),
  };

  if (newContent) {
    updates.content = newContent;
    updates.status = 'in_progress';
  }

  await updateDoc(docRef, updates);
};

export const setTenderSectionSuggestion = async (
  tenderId: string,
  sectionId: string,
  suggestion: string
) => {
  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);
  await updateDoc(docRef, {
    suggested_content: suggestion,
    has_suggestion: true,
    updatedAt: new Date(),
  });
};

export const fetchCVSectionFields = async (tenderId: string, sectionId: string) => {
  const fieldsRef = collection(firestore, 'tenders', tenderId, 'sections', sectionId, 'fields');
  const querySnapshot = await getDocs(fieldsRef);
  return querySnapshot.docs.map((doc) => doc.data());
};

export const saveCVSectionField = async (
  tenderId: string,
  sectionId: string,
  fieldKey: string,
  content: unknown // Can be string or object/array, will be JSON stringified if needed
) => {
  // Ensure content is a string for save
  const stringContent = typeof content === 'string' ? content : JSON.stringify(content);

  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId, 'fields', fieldKey);
  await setDoc(
    docRef,
    {
      key: fieldKey,
      content: stringContent,
      updatedAt: new Date(),
    },
    { merge: true }
  );
};

export const getSectionRevisions = async (
  tenderId: string,
  sectionId: string
): Promise<SectionRevision[]> => {
  const revisionsRef = collection(
    firestore,
    'tenders',
    tenderId,
    'sections',
    sectionId,
    'revisions'
  );
  const snapshot = await getDocs(revisionsRef);

  return snapshot.docs
    .map((doc) => doc.data() as SectionRevision)
    .sort((a, b) => {
      // Sort descending by date
      const dateA =
        a.updatedAt instanceof Date ? a.updatedAt.getTime() : new Date(a.updatedAt).getTime();
      const dateB =
        b.updatedAt instanceof Date ? b.updatedAt.getTime() : new Date(b.updatedAt).getTime();
      return dateB - dateA;
    });
};

export const restoreSectionRevision = async (
  tenderId: string,
  sectionId: string,
  revision: SectionRevision,
  restoredBy: SectionUserRef
) => {
  // To restore, we effectively "save" the old content as the new content.
  // We don't need to re-encrypt because we treat the encrypted blob as opaque.
  // EXCEPT: The encryption key used for the revision MUST be the same as the current key.
  // Since we use a single TEK (Tender Encryption Key) per tender, this is valid.
  // If we rotated keys per version, this would be complex. We assume single TEK.

  const docRef = doc(firestore, 'tenders', tenderId, 'sections', sectionId);

  await updateDoc(docRef, {
    encrypted_content: revision.encrypted_content,
    updatedAt: new Date(),
    lastEditedBy: restoredBy,
    editHistory: arrayUnion(restoredBy),
  });

  // Optionally verify: Should we create a revision for the "restore action" itself?
  // Yes, standard practice. The "restored" state is a new state.
  const revisionsRef = collection(
    firestore,
    'tenders',
    tenderId,
    'sections',
    sectionId,
    'revisions'
  );
  const newRevisionRef = doc(revisionsRef);
  await setDoc(newRevisionRef, {
    id: newRevisionRef.id,
    sectionId,
    encrypted_content: revision.encrypted_content,
    updatedAt: new Date(),
    editor: restoredBy,
    revisionNumber: Date.now(),
    restoredFrom: revision.id,
  });
};

export const listenToTenderSections = (
  tenderId: string,
  onUpdate: (sections: Section[]) => void,
  onError: (error: Error) => void
) => {
  const sectionsRef = collection(firestore, 'tenders', tenderId, 'sections');
  return onSnapshot(
    sectionsRef,
    (snapshot) => {
      const sections = snapshot.docs.map((doc) => ({
        ...(doc.data() as Section),
        id: doc.id,
      }));
      onUpdate(sections);
    },
    onError
  );
};
